import packageJson from "../../../package.json";
import {
  ErrorKind,
  ErrorReport,
  UsageReport,
  ViewKind,
  ViewUsage,
} from "types/analytics";
import { SESSION_ID, SESSION_START, sessionDurationSeconds } from "./session";

/**
 * Accumulates usage counters in memory between MQTT publishes.
 *
 * Everything is a counter rather than an event stream: recording a press is a
 * single integer increment, and each report is a small fixed-size JSON document
 * no matter how busy the session was. Nothing is written to localStorage or
 * sessionStorage.
 */

/** Hard caps so a misbehaving session can never grow the buffer without bound */
const MAX_ERRORS = 25;
const MAX_TRACKED_VIEWS = 100;
const MAX_TRACKED_EVENTS = 100;
const MAX_TRACKED_PRESSES = 100;
const MESSAGE_MAX_LENGTH = 300;
const STACK_MAX_LENGTH = 600;

interface OpenView {
  kind: ViewKind;
  name: string;
  startedAt: number;
}

const truncate = (value: string | null | undefined, max: number) => {
  if (!value) return null;
  return value.length > max ? value.slice(0, max) + "…" : value;
};

export class UsageBuffer {
  private screens: Record<string, ViewUsage> = {};
  private widgets: Record<string, ViewUsage> = {};
  private presses: Record<string, number> = {};
  private events: Record<string, number> = {};
  private errors: ErrorReport[] = [];

  private droppedErrors = 0;
  private sequence = 0;
  private windowStartedAt = Date.now();

  private openViews: Record<number, OpenView> = {};
  private nextViewHandle = 1;

  private counters(kind: ViewKind) {
    return kind === ViewKind.Screen ? this.screens : this.widgets;
  }

  /**
   * Marks a view as entered. Returns a handle to pass to `endView`; view time is
   * attributed to whichever reporting windows the view was open during.
   */
  beginView(kind: ViewKind, name: string): number {
    const counters = this.counters(kind);

    if (!counters[name]) {
      if (Object.keys(counters).length >= MAX_TRACKED_VIEWS) return 0;
      counters[name] = { views: 0, seconds: 0 };
    }
    counters[name].views += 1;

    const handle = this.nextViewHandle++;
    this.openViews[handle] = { kind, name, startedAt: Date.now() };
    return handle;
  }

  endView(handle: number): void {
    const open = this.openViews[handle];
    if (!open) return;

    this.addViewSeconds(open, Date.now());
    delete this.openViews[handle];
  }

  private addViewSeconds(open: OpenView, until: number) {
    const counters = this.counters(open.kind);
    const usage = counters[open.name];
    if (!usage) return;

    usage.seconds += Math.max(0, (until - open.startedAt) / 1000);
  }

  /** A press of a named UI control, counted automatically per control. */
  recordPress(name: string): void {
    if (this.presses[name] === undefined) {
      if (Object.keys(this.presses).length >= MAX_TRACKED_PRESSES) return;
      this.presses[name] = 0;
    }
    this.presses[name] += 1;
  }

  recordEvent(name: string): void {
    if (this.events[name] === undefined) {
      if (Object.keys(this.events).length >= MAX_TRACKED_EVENTS) return;
      this.events[name] = 0;
    }
    this.events[name] += 1;
  }

  /** Identical errors collapse into a single entry with a count. */
  recordError(error: {
    kind: ErrorKind;
    message: string;
    source?: string | null;
    line?: number | null;
    column?: number | null;
    stack?: string | null;
  }): void {
    const message =
      truncate(error.message, MESSAGE_MAX_LENGTH) ?? "Unknown error";
    const source = truncate(error.source, MESSAGE_MAX_LENGTH);
    const line = error.line ?? null;
    const column = error.column ?? null;
    const timestamp = new Date().toISOString();

    for (let i = 0; i < this.errors.length; i++) {
      const existing = this.errors[i];
      if (
        existing.kind === error.kind &&
        existing.message === message &&
        existing.source === source &&
        existing.line === line
      ) {
        existing.count += 1;
        existing.last_timestamp = timestamp;
        return;
      }
    }

    if (this.errors.length >= MAX_ERRORS) {
      this.droppedErrors += 1;
      return;
    }

    this.errors.push({
      kind: error.kind,
      message,
      source,
      line,
      column,
      stack: truncate(error.stack, STACK_MAX_LENGTH),
      count: 1,
      first_timestamp: timestamp,
      last_timestamp: timestamp,
    });
  }

  /** True when there is anything worth publishing. */
  hasData(): boolean {
    return (
      Object.keys(this.screens).length > 0 ||
      Object.keys(this.widgets).length > 0 ||
      Object.keys(this.presses).length > 0 ||
      Object.keys(this.events).length > 0 ||
      this.errors.length > 0 ||
      this.droppedErrors > 0 ||
      Object.keys(this.openViews).length > 0
    );
  }

  /**
   * Builds the report for the window that just closed and resets the counters.
   * Views still open keep accumulating into the next window.
   */
  drain(
    tripId: string | null,
    deviceId: string | null = null,
    vesselId: string | null = null,
    final = false
  ): UsageReport {
    const now = Date.now();

    // Credit views that are still open with the time they have been open, then
    // restart their clock so the next window starts from here
    const handles = Object.keys(this.openViews);
    for (let i = 0; i < handles.length; i++) {
      const open = this.openViews[Number(handles[i])];
      this.addViewSeconds(open, now);
      open.startedAt = now;
    }

    const report: UsageReport = {
      type: "usage",
      session_id: SESSION_ID,
      device_id: deviceId,
      vessel_id: vesselId,
      software_version: packageJson.version,
      sequence: ++this.sequence,
      final,
      timestamp: new Date(now).toISOString(),
      session_start_timestamp: SESSION_START.toISOString(),
      session_duration_seconds: sessionDurationSeconds(),
      window_seconds: Math.max(
        0,
        Math.round((now - this.windowStartedAt) / 1000)
      ),
      trip_id: tripId,
      screens: roundViewSeconds(this.screens),
      widgets: roundViewSeconds(this.widgets),
      presses: this.presses,
      events: this.events,
      errors: this.errors,
      dropped_errors: this.droppedErrors,
    };

    this.screens = {};
    this.widgets = {};
    this.presses = {};
    this.events = {};
    this.errors = [];
    this.droppedErrors = 0;
    this.windowStartedAt = now;

    // Re-register the still-open views so their remaining time keeps being
    // attributed, without counting as new views
    for (let i = 0; i < handles.length; i++) {
      const open = this.openViews[Number(handles[i])];
      const counters = this.counters(open.kind);
      if (!counters[open.name]) {
        counters[open.name] = { views: 0, seconds: 0 };
      }
    }

    return report;
  }
}

/**
 * Rounds view seconds to one decimal and drops entries where nothing happened
 * this window — a view carried over from the previous window but neither entered
 * nor open for any measurable time has nothing to report.
 */
const roundViewSeconds = (counters: Record<string, ViewUsage>) => {
  const rounded: Record<string, ViewUsage> = {};
  const names = Object.keys(counters);

  for (let i = 0; i < names.length; i++) {
    const usage = counters[names[i]];
    const seconds = Math.round(usage.seconds * 10) / 10;

    if (usage.views === 0 && seconds === 0) continue;

    rounded[names[i]] = { views: usage.views, seconds };
  }

  return rounded;
};
